import { AfterContentInit, Component, OnDestroy, OnInit } from '@angular/core';
import { faTimes, faQuestion } from '@fortawesome/free-solid-svg-icons';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { ValidationErrors } from "@angular/forms/src/directives/validators";
import { ExchangeAvailableCoin } from "../models/ExchangeAvailableCoin.model";
import { ExchangeInfo } from "../models/ExchangeInfo.model";
import { CoinPurchaseInfoService } from "../services/coinPurchaseInfo.service";
import { buildCatchingApiError, buildCatchingHttpError } from "@bt/core/utils/catchingException";
import { IResponse } from "@bt/shared";
import { NotifierService } from "angular-notifier";
import { getMinOrEqualsValidator } from "@bt/core/utils/validateFunctions";
import { BaseComponent, JobInfo, UserModel } from "@bt/core";
import { AutoCoinPurchaseParameters } from "../models/AutoCoinPurchaseParameters.model";
import { ActivatedRoute } from "@angular/router";
import { StoredJobService } from "@bt/core/services/storedJob.service";

@Component({
  selector: 'app-job-parameters-component',
  templateUrl: './job-parameters-component.html',
  styleUrls: ['./job-parameters-component.scss']
})
export class JobParametersModalComponent extends BaseComponent implements OnInit, AfterContentInit, OnDestroy {
  private readonly jobGroup = "AutoCoinPurchase";
  private readonly jobType = "Bt.Modules.Main.BackgroundJobs.CoinsPurchaseInitJob";

  private readonly timespanPattern = /^(\d+\.)?([0-1][0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/

  private isCaughtApiError: (response: IResponse<any>) => boolean;
  private catchRequestError: (error: { message: string }) => void;

  protected readonly faTimes = faTimes;
  protected readonly faQuestion = faQuestion;

  protected jobParametersForm: FormGroup;
  protected isLoading: boolean = false;
  private exchangeChangeEventEnabled: boolean = true;

  protected availableCoins: ExchangeAvailableCoin[] = [];
  protected selectedAssetId: string;

  protected usersForWithdraw: UserModel[] = [];
  protected selectedUserId: string;

  protected exchanges: ExchangeInfo[] = [{ id: 2, code: 'test1', name: 'test' }];
  protected selectedExchangeId: string;

  protected jobSettings?: JobInfo<AutoCoinPurchaseParameters>;

  constructor(
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private storedJobService: StoredJobService,
    private coinPurchaseInfoService: CoinPurchaseInfoService,
    private notifierService: NotifierService,
  ) {
    super();
    const me = this;

    me.route.queryParams.subscribe(params => {
      if (params['name']) {
        me.jobSettings = {
          ...params,
          parameters: JSON.parse(params['parameters']),
        } as JobInfo<AutoCoinPurchaseParameters>;
      }
    });

    me.isCaughtApiError = buildCatchingApiError(me.notifierService);
    me.catchRequestError = buildCatchingHttpError(me.notifierService, () => me.isLoading = false);
  }

  ngOnInit() {
    this.jobParametersForm = this.formBuilder.group({
      taskName: ["", [Validators.required]],
      exchange: ["", [Validators.required]],
      sourceAsset: ["", [Validators.required]],
      minAssetAmount: [0, [getMinOrEqualsValidator(0)]],
      maxPurchaseAssetAmount: [0, [Validators.min(0)]],
      maxBaseAssetAmount: [0, [Validators.min(0)]],
      userForWithdraw: [null],
      coins: new FormArray([], [this.coinsValidate]),
      coinPercents: new FormArray([], [this.coinPercentsValidate]),
      interval: ["1.00:00:00", [Validators.required, Validators.pattern(this.timespanPattern)]],
      firstRunTime: [""],
    });
  }

  ngAfterContentInit() {
    this.loadUsers();

    const exchangesLoading = this.loadExchanges();
    if (this.jobSettings) {
      this.loadJobSettings(this.jobSettings, exchangesLoading);
    }
  }

  ngOnDestroy(): void {
    this.jobParametersForm.reset();
  }

  /**
   * Возвращает контроллеры компонентов ввода формы
   * @protected
   */
  protected get formControls() {
    return this.jobParametersForm.controls;
  }

  /**
   * Возвращает текст ошибки, если не выбрана ни одна монета для покупки
   * @protected
   */
  protected get coinsErrorText() {
    return this.formControls.coins.errors
      && this.formControls.coins.errors.required &&
      "Необходимо выбрать хотя бы одну монету";
  }

  /**
   * Собирает информацию о выбранных монетах: количеситво и сумма процентов
   * @protected
   */
  protected get selectedCoinsInfo(): { percentSum: number; coinCount: number; } {
    const resultInfo = { percentSum: 0, coinCount: 0 };

    (this.jobParametersForm.get('coinPercents') as FormArray).controls
      .forEach((control) => {
        if (control.enabled) {
          resultInfo.coinCount++;
          resultInfo.percentSum += control.value;
        }
      });

    return resultInfo;
  }

  /**
   * Определяет, правильно ли заполнен компонент ввода
   * @param elem Компонент ввода
   * @override
   */
  public elemIsInvalid(elem: AbstractControl): boolean {
    return elem.invalid;
  }

  /**
   * Формирует текст ошибки компонента ввода
   * @param elem Компонент ввода
   * @param namepattern Понятное пользователю представление паттерна
   * @override
   */
  public textErrorStr(elem: AbstractControl, namepattern = null): string {
    if (!this.elemIsInvalid(elem))
      return "";

    if (elem.errors.sumMax)
      return "Сумма долей монет не может превышать 100%";

    if (elem.errors.sumMin)
      return "Сумма долей монет не может быть меньше или равной 0%";

    const errorIndexes = elem.errors.errorIndexes;
    if (errorIndexes && errorIndexes.length) {
      const joinedCoins = errorIndexes
        .map(index => `'${this.availableCoins[index].coinName}'`)
        .join(", ");
      return `Процент монет: ${joinedCoins} - должен быть в интервале от 0 (не включая) до 100 (включительно)`;
    }

    return super.textErrorStr(elem, namepattern);
  }

  /**
   * Запускает фоновую задачу инициализации автозакупки по условиям
   * @protected
   */
  protected runStoredJob() {
    const me = this;

    const firstRunTimeValue = me.jobParametersForm.get('firstRunTime').value;
    const jobParameters: AutoCoinPurchaseParameters = {
      exchangeId: Number.parseInt(me.jobParametersForm.get('exchange').value),
      assetId: Number.parseInt(me.jobParametersForm.get('sourceAsset').value),
      minExchangeBalance: Number.parseFloat(me.jobParametersForm.get('minAssetAmount').value),
      maxExchangeAmount: Number.parseFloat(me.jobParametersForm.get('maxPurchaseAssetAmount').value),
      maxBaseAssetAmount: Number.parseFloat(me.jobParametersForm.get('maxBaseAssetAmount').value),
      userForWithdraw: me.jobParametersForm.get('userForWithdraw').value || null,
      purchasingCoins: (me.jobParametersForm.get('coins') as FormArray)
        .controls.map((control, index) => ({
          coinId: control.value ? me.availableCoins[index].id : 0,
          purchasingPercent: me.jobParametersForm.get(['coinPercents', index]).value,
        }))
        .filter(x => x.coinId > 0),
    };

    if (me.jobSettings) {
      me.storedJobService.updateStoredJob({
          jobName: me.jobParametersForm.get('taskName').value,
          jobGroup: me.jobGroup,
          jobInterval: me.jobParametersForm.get('interval').value,
          firstRunningDateTime: firstRunTimeValue ? new Date(firstRunTimeValue) : null,
          parameters: JSON.stringify(jobParameters),
        })
        .then(response => {
          if (!me.isCaughtApiError(response)) {
            me.notifierService.notify("success", "Задача изменена успешно");
          }
        })
        .catch(me.catchRequestError);
    } else {
      me.storedJobService.createStoredJob({
          jobType: me.jobType,
          jobName: me.jobParametersForm.get('taskName').value,
          jobGroup: me.jobGroup,
          jobInterval: me.jobParametersForm.get('interval').value,
          firstRunningDateTime: firstRunTimeValue ? new Date(firstRunTimeValue) : null,
          parameters: JSON.stringify(jobParameters),
        })
        .then(response => {
          if (!me.isCaughtApiError(response)) {
            me.notifierService.notify("success", "Задача создана успешно");
          }
        })
        .catch(me.catchRequestError);
    }
  }

  /**
   * Устанавливает значения полей формы в соответствии с настройками задачи
   * @private
   */
  private loadJobSettings(
    jobSettings: JobInfo<AutoCoinPurchaseParameters>,
    exchangesLoading: Promise<void>
  ) {
    const me = this;

    let taskNameControl = me.jobParametersForm.get('taskName');
    taskNameControl.setValue(jobSettings.name);
    taskNameControl.disable();
    me.jobParametersForm.get('minAssetAmount').setValue(jobSettings.parameters.minExchangeBalance);
    me.jobParametersForm.get('maxPurchaseAssetAmount').setValue(jobSettings.parameters.maxExchangeAmount || 0);
    me.jobParametersForm.get('maxBaseAssetAmount').setValue(jobSettings.parameters.maxBaseAssetAmount || 0);

    me.jobParametersForm.get('userForWithdraw').setValue(jobSettings.parameters.userForWithdraw);
    me.selectedUserId = jobSettings.parameters.userForWithdraw;

    me.jobParametersForm.get('interval').setValue(jobSettings.interval);

    // Загрузка и установка выбранной биржи
    exchangesLoading.then(() => {
      if (!me.exchanges || !me.exchanges.length) {
        me.notifierService.notify("error", "Не удалось загрузить все параметры задачи");
        return;
      }

      const exchangeId = jobSettings.parameters.exchangeId;
      const exchangeExists = me.exchanges.findIndex(x => x.id === exchangeId) > -1;
      if (!exchangeExists) {
        me.notifierService.notify("error", "Биржа не найдена в списке бирж");
        return;
      }

      me.exchangeChangeEventEnabled = false;
      me.selectedExchangeId = `${exchangeId}`;

      // Загрузка и установка актива и покупаемых монет
      me.loadAvailableCoinInfo(exchangeId)
        .then(() => {
          if (!me.availableCoins || !me.availableCoins.length) {
            me.notifierService.notify("error", "Не удалось загрузить все параметры задачи");
            return;
          }

          // Установка актива
          const assetId = jobSettings.parameters.assetId;
          const assetExists = me.availableCoins.findIndex(x => x.id === assetId) > -1;
          if (!assetExists) {
            me.notifierService.notify("error", "Актив не найден в списке доступных монет");
          } else {
            me.selectedAssetId = `${assetId}`;
          }

          // Установка чекбоксов для выбранных монет для покупки
          const checkedCoinIds = jobSettings.parameters.purchasingCoins.map(x => x.coinId);
          const coinsControl = me.jobParametersForm.get('coins');
          coinsControl && coinsControl.setValue(
            me.availableCoins.map(coin => checkedCoinIds.indexOf(coin.id) > -1)
          );

          // Установка доли закупки в виде процента
          const coinPercentsControl = me.jobParametersForm.get('coinPercents') as FormArray;
          me.availableCoins.forEach((coin, index) => {
            const purchasingCoin = jobSettings.parameters.purchasingCoins
              .find(x => x.coinId === coin.id);

            if (purchasingCoin) {
              coinPercentsControl.controls[index].enable();
              coinPercentsControl.controls[index].setValue(purchasingCoin.purchasingPercent || 0);
            } else {
              coinPercentsControl.controls[index].disable();
            }
          });

          me.exchangeChangeEventEnabled = true;
        });
    });
  }

  /**
   * Загружает доступные криптобиржи для выбора
   * @protected
   */
  protected loadExchanges(): Promise<void> {
    const me = this;

    me.isLoading = true;

    me.exchanges = [];
    const exchangeControl = me.jobParametersForm.get('exchange');
    exchangeControl && exchangeControl.setValue('');

    return me.coinPurchaseInfoService.getExchanges()
      .then((response) => {
        me.isLoading = false;
        if (me.isCaughtApiError(response))
          return;

        me.exchanges = response.data || [];
      })
      .catch(me.catchRequestError);
  }

  /**
   * Загружает информацию о доступных для покупки монет на бирже
   * @param exchangeId Идентификатор биржи
   * @protected
   */
  protected loadAvailableCoinInfo(exchangeId: number): Promise<void> {
    const me = this;

    me.isLoading = true;

    me.availableCoins = [];
    const sourceAssetControl = me.jobParametersForm.get('sourceAsset');
    sourceAssetControl && sourceAssetControl.setValue('');
    me.jobParametersForm.setControl('coins', new FormArray([]));
    me.jobParametersForm.setControl('coinPercents', new FormArray([]));

    if (Number.isNaN(exchangeId)) {
      return new Promise<void>((resolve) => resolve());
    }

    return me.coinPurchaseInfoService.getAvailableCoins(exchangeId)
      .then((response) => {
        me.isLoading = false;
        if (me.isCaughtApiError(response))
          return;

        me.availableCoins = response.data || [];
        me.jobParametersForm.setControl(
          'coins',
          new FormArray(
            me.availableCoins.map(_ => new FormControl(false)),
            me.coinsValidate));
        me.jobParametersForm.setControl(
          'coinPercents',
          new FormArray(
            me.availableCoins.map(_ => {
              const control = new FormControl(0);
              control.disable();
              return control;
            }),
            me.coinPercentsValidate));
      })
      .catch(me.catchRequestError);
  }

  /**
   * Загружает список пользователей для выбора,
   * от чьего имени будет производиться операция вывода средств из биржи
   * @protected
   */
  protected loadUsers() {
    const me = this;

    me.isLoading = true;
    me.coinPurchaseInfoService.getUserList()
      .then((response) => {
        me.isLoading = false;
        if (me.isCaughtApiError(response))
          return;

        me.usersForWithdraw = response.data || [];
      })
      .catch(me.catchRequestError);
  }

  /**
   * После выбора биржи загружает список доступных для покупки монет
   * @param exchangeId
   * @protected
   */
  protected exchangeChanged(exchangeId: string) {
    if (this.exchangeChangeEventEnabled)
      this.loadAvailableCoinInfo(Number.parseInt(exchangeId));
  }

  /**
   * Блокирует поле для ввода доли монеты для неактивных монет,
   * а для активных - разрешает ввод доли монеты в виде процента
   * @protected
   */
  protected coinCheckboxChanged(newChecked: boolean, index: number) {
    const coinPercentControl = this.jobParametersForm.get(['coinPercents', `${index}`]);

    if (newChecked)
      coinPercentControl.enable();
    else {
      coinPercentControl.setValue(0);
      coinPercentControl.disable();
    }
  }

  /**
   * Осуществляют валидацию списка монет для закупки, чтобы хотя бы одна монета была выбрана
   * @param coinsFormArray
   * @private
   */
  private coinsValidate(coinsFormArray: FormArray): ValidationErrors {
    if (!~coinsFormArray.value.findIndex(x => x)) {
      return {
        required: true,
      };
    }

    return {};
  }

  /**
   * Осуществляют валидацию списка монет для закупки, чтобы доля каждой монеты была положительная
   * и сумма долей была не больше 100
   * @param coinPercentsFormArray Значения долей покупаемых монет
   * @private
   */
  private coinPercentsValidate(coinPercentsFormArray: FormArray): ValidationErrors {
    let percentSum = 0;
    const errorIndexes = [];
    coinPercentsFormArray.controls.forEach((control, index) => {
      if (!control.enabled) {
        return;
      }

      let percentValue = control.value;
      percentSum += percentValue;
      if ((percentValue > 100 || percentValue <= 0)) {
        errorIndexes.push(index);
      }
    });

    const resultErrors = {};
    if (errorIndexes.length) {
      resultErrors['errorIndexes'] = errorIndexes;
    }
    if (percentSum > 100) {
      resultErrors['sumMax'] = true;
    }
    if (percentSum <= 0) {
      resultErrors['sumMin'] = true;
    }

    return resultErrors;
  }
}



// WEBPACK FOOTER //
// ./src/app/auto-coin-purchase/job-parameters/job-parameters-component.ts